import { useState, useEffect } from "react"
import { Plus } from "lucide-react"
import { Button } from "components/ui/button"
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "components/ui/sheet"
import { CalendarToolbar } from "./CalendarToolbar"
import { CalendarMonthView } from "./CalendarMonthView"
import { CalendarWeekView } from "./CalendarWeekView"
import { CalendarDayView } from "./CalendarDayView"
import { NewEventModal } from "./NewEventModal"
import { EventDetailModal } from "./EventDetailModal"
import type { CalendarEvent, FilterState, ViewMode } from "../../types/agenda"
import "./agenda-responsive.css"

interface AgendaContentProps {
  events: CalendarEvent[]
  onCreateEvent: (event: CalendarEvent) => void
  onUpdateEvent?: (id: number, event: Partial<CalendarEvent>) => Promise<void>
  onDeleteEvent?: (id: number) => Promise<void>
  loading?: boolean
  workingHours?: string
  highlightedEventId?: number | null
  openDetailId?: number | null
}

export function AgendaContent({
  events,
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  loading = false,
  workingHours,
  highlightedEventId,
  openDetailId
}: AgendaContentProps) {
  const [viewMode, setViewMode] = useState<ViewMode>(() => window.matchMedia("(max-width: 767px)").matches ? "Day" : "Month")
  const [currentDate, setCurrentDate] = useState(new Date())
  const [searchQuery, setSearchQuery] = useState("")
  const [filters, setFilters] = useState<FilterState>({
    eventType: [],
    participation: [],
  })
  const [isNewEventModalOpen, setIsNewEventModalOpen] = useState(false)
  const [newEventInitialDate, setNewEventInitialDate] = useState<Date | undefined>(undefined)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [isEventDetailModalOpen, setIsEventDetailModalOpen] = useState(false)

  // Sincronizza selectedEvent quando events cambia (dopo edit/save)
  useEffect(() => {
    if (selectedEvent) {
      const updated = events.find(e => e.id === selectedEvent.id)
      if (updated) {
        setSelectedEvent(updated)
      }
    }
  }, [events])

  // Apri il side-panel se si arriva dalla ricerca globale con openDetailId
  useEffect(() => {
    if (openDetailId && events.length > 0) {
      const event = events.find(e => e.id === openDetailId)
      if (event) {
        setSelectedEvent(event)
        setIsEventDetailModalOpen(true)
      }
    }
  }, [openDetailId, events])

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event)
    setIsEventDetailModalOpen(true)
  }

  const handleEmptyCellClick = (date: Date) => {
    setNewEventInitialDate(date)
    setIsNewEventModalOpen(true)
  }

  const handleUpdateEvent = async (id: number, data: Partial<CalendarEvent>) => {
    if (onUpdateEvent) {
      await onUpdateEvent(id, data)
    }
  }

  const handleDeleteEvent = async (id: number) => {
    if (onDeleteEvent) {
      await onDeleteEvent(id)
      setIsEventDetailModalOpen(false)
    }
  }

  // Filter events based on search and filters
  const filteredEvents = events.filter((event) => {
    // Event type filter: if any selected, show only those types
    if (filters.eventType.length > 0 && !filters.eventType.includes(event.type)) {
      return false
    }

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase()
      return (
        event.title?.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.location?.toLowerCase().includes(searchLower)
      )
    }

    return true
  })

  const handleSaveEvent = (savedEvent: CalendarEvent) => {
    // L'evento è già stato salvato da NewEventModal, passa al parent per aggiornare lo state
    onCreateEvent(savedEvent)
  }

  if (loading) {
    return (
      <div className="flex-1 p-6 max-w-7xl w-full mx-auto flex items-center justify-center">
        <div className="text-[var(--hexa-text-primary)] text-lg">Caricamento eventi...</div>
      </div>
    )
  }

  return (
    <div className="agenda-page flex-1 min-w-0 p-3 sm:p-6 pb-28 sm:pb-28 max-w-7xl w-full mx-auto flex flex-col gap-3">
      {/* Action Button - "+ Nuovo" */}
      <div className="flex justify-end">
        <Button
          onClick={() => { setNewEventInitialDate(undefined); setIsNewEventModalOpen(true) }}
          className="h-10 px-3 bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white font-medium"
          aria-label="Nuovo evento"
        >
          <Plus className="h-3.5 w-3.5 mr-0.5" />
          Nuovo
        </Button>
      </div>

      {/* Calendar Toolbar */}
      <CalendarToolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Calendar Views */}
      {viewMode === "Month" && (
        <CalendarMonthView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={handleEventClick}
          onDayClick={(date) => {
            setCurrentDate(date)
            setViewMode("Day")
          }}
          onEmptyCellClick={handleEmptyCellClick}
          highlightedEventId={highlightedEventId}
        />
      )}
      {viewMode === "Week" && (
        <CalendarWeekView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={handleEventClick}
          onEmptyCellClick={handleEmptyCellClick}
          workingHours={workingHours}
          highlightedEventId={highlightedEventId}
        />
      )}
      {viewMode === "Day" && (
        <CalendarDayView
          currentDate={currentDate}
          events={filteredEvents}
          onEventClick={handleEventClick}
          onEmptyCellClick={handleEmptyCellClick}
          workingHours={workingHours}
          highlightedEventId={highlightedEventId}
        />
      )}

      {/* New Event Modal Sheet */}
      <Sheet open={isNewEventModalOpen} onOpenChange={setIsNewEventModalOpen}>
        <SheetContent className="agenda-sheet !w-full sm:!max-w-[380px] h-[100dvh] min-w-0 overflow-hidden p-0 gap-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Nuovo Evento</SheetTitle>
          <SheetDescription className="sr-only">Crea un nuovo evento</SheetDescription>
          <NewEventModal
            onClose={() => setIsNewEventModalOpen(false)}
            onSave={handleSaveEvent}
            initialDate={newEventInitialDate}
          />
        </SheetContent>
      </Sheet>

      {/* Event Detail Modal Sheet */}
      <Sheet open={isEventDetailModalOpen} onOpenChange={setIsEventDetailModalOpen}>
        <SheetContent className="agenda-sheet !w-full sm:!max-w-[380px] h-[100dvh] min-w-0 overflow-hidden p-0 gap-0 bg-[var(--hexa-modal-bg)] border-[var(--hexa-border)]">
          <SheetTitle className="sr-only">Dettaglio Evento</SheetTitle>
          <SheetDescription className="sr-only">Visualizza o modifica evento</SheetDescription>
          {selectedEvent && (
            <EventDetailModal
              event={selectedEvent}
              onClose={() => setIsEventDetailModalOpen(false)}
              onUpdate={handleUpdateEvent}
              onDelete={handleDeleteEvent}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
